import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@appkit4/react-components';
import './Chatbot.scss';
import ChatbotImg from '../../../assets/illustrations/Chatbot.png';
import { useChatBot } from '../../../helpers/useChatBot';
import { ChatWithExtractionResponse } from '../../../api/generated/models';
import { useParams } from 'react-router-dom';

const Chatbot: React.FC = () => {
    const [messages, setMessages] = useState<
        { content: string; role: string }[]
    >([]);
    const [userInput, setUserInput] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const { chatWithAI } = useChatBot();
    const { processId } = useParams<{ processId: string }>();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    const toggleChatWindow = () => {
        if (isOpen) {
            setIsHovered(false);
        }
        setIsOpen(!isOpen);
    };

    const sendMessage = async () => {
        if (userInput.trim()) {
            const newMessage = { role: 'user', content: userInput };
            setMessages([...messages, newMessage]);
            setUserInput('');
            setLoading(true);

            try {
                const botMessageReply = await botReply(
                    messages,
                    newMessage.content
                );
                setMessages((prev) => [...prev, botMessageReply]);
            } catch (error) {
                console.error('Error sending message:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !isLoading) {
            event.preventDefault();
            sendMessage();
        }
    };

    const botReply = async (messages: any, text: string) => {
        const messagesToBot = [...messages, { role: 'user', content: text }];
        const chatBotReply: ChatWithExtractionResponse = await chatWithAI(
            Number(processId),
            messagesToBot
        );
        return { content: `${chatBotReply.lastMessage}`, role: 'assistant' };
    };

    return (
        <div className="chatbot-container">
            {isOpen ? (
                <div className="chat-window">
                    <div className="chat-header">
                        <div className="chat-title">
                            <img
                                src={ChatbotImg}
                                alt="AI Assistant"
                                className="header-icon"
                            />
                            <h2>AI Assistant</h2>
                        </div>
                        <span
                            className="Appkit4-icon icon-close-outline"
                            onClick={toggleChatWindow}
                        ></span>
                    </div>
                    <div className="messages">
                        {messages.length === 0 ? (
                            <div className="empty-state">
                                <img
                                    src={ChatbotImg}
                                    alt="Welcome"
                                    className="welcome-icon"
                                />
                                <h3>Welcome to AI Assistant!</h3>
                                <p>
                                    Ask me anything about your process or
                                    documents.
                                </p>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg, index) => (
                                    <div
                                        key={index}
                                        className={`message ${msg.role}`}
                                    >
                                        {msg.role === 'assistant' && (
                                            <img
                                                src={ChatbotImg}
                                                alt="bot"
                                                className="chatbot-icon"
                                            />
                                        )}
                                        <span>{msg.content}</span>
                                    </div>
                                ))}
                                {isLoading && (
                                    <div className="message assistant typing">
                                        <img
                                            src={ChatbotImg}
                                            alt="bot"
                                            className="chatbot-icon"
                                        />
                                        <div className="typing-indicator">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                        </div>
                                    </div>
                                )}
                                <div
                                    ref={messagesEndRef}
                                    className="messages-end"
                                />
                            </>
                        )}
                    </div>
                    <div className="input-area">
                        <textarea
                            disabled={isLoading}
                            value={userInput}
                            placeholder="Type your message here..."
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            rows={1}
                            className="chat-input"
                        />
                        <Button
                            onClick={sendMessage}
                            disabled={isLoading || !userInput.trim()}
                            className="send-button"
                        >
                            <span className="Appkit4-icon icon-arrow-up-fill"></span>
                        </Button>
                    </div>
                </div>
            ) : (
                <div
                    className={`chat-icon ${isHovered ? 'hovered' : ''}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={toggleChatWindow}
                >
                    {isHovered && <span>AI Chatbot</span>}
                    <img src={ChatbotImg} alt="Chatbot" />
                </div>
            )}
        </div>
    );
};

export default Chatbot;
